import useAgentHubCommission from 'agentHub/hooks/useAgentHubCommissionV2'
import FormatCurrency from 'components/Common/FormatCurrency'
import PriceRowCommission from 'LuxKit/PricePoints/PriceRowCommission'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Group from 'components/utils/Group'

interface Props {
  productType: App.ProductType
  kind?: 'default' | 'plain'
  offerId: string
  packageId?: string
  offerLocations?: Array<string>
  vendorName?: string
  offerTotal?: number
  /**
   * optional, should be used when there are multiple options inside offer
   * (hotel packages, tour variations, etc.) so that we can get a date range commission
   */
  optionId?: string
  offer?: App.AgentHubOfferPayload
  /** When provided, dates/rooms are used instead of URL params for commission fetching (e.g. tour accommodation). */
  checkIn?: string
  checkOut?: string
  rooms?: Array<App.Occupants>
  bundledTypes?: Array<string>
}

function PriceRowAgentHubCommission(props: Props) {
  const {
    productType,
    kind = 'default',
    offerId,
    offerTotal = 0,
    packageId,
    optionId,
    offer,
    checkIn,
    checkOut,
    rooms,
    bundledTypes,
  } = props

  const dateOverrides = useMemo(
    () => (checkIn && checkOut && rooms ? { checkIn, checkOut, rooms } : undefined),
    [checkIn, checkOut, rooms],
  )

  const customerView = useAppSelector(state => state.agentHub.customerView)
  const {
    commissionPercentage,
    packagesCommission,
    greatestPackageCommission,
    minBaseCommission,
    maxBaseCommission,
    dateRangesCommission,
    includesTaxesAndFees,
    incentiveCommissionPercentage,
    incentiveCommissionMaxAmount,
  } = useAgentHubCommission(offerId, offer, dateOverrides, bundledTypes)
  const commissionLoading = useAppSelector(state => state.agentHub.offersCommissionLoading[offerId])
  const packageCommission = packageId ? packagesCommission.find(pkg => pkg.packageId === packageId) : null
  const dateRangeCommission = optionId ? dateRangesCommission[optionId] : null

  const greatestCommissionWithDateRange = useMemo(() => {
    const greatestDateRangeCommission = Math.max(0, ...Object.values(dateRangesCommission).map(commission => commission.commissionPercentage))
    return greatestDateRangeCommission + commissionPercentage
  }, [dateRangesCommission, commissionPercentage])

  const commissionToDisplay = useMemo(() => {
    const dateRangeCommissionPercentage = dateRangeCommission?.commissionPercentage ?? 0
    if (packageCommission?.commissionPercentage) {
      return packageCommission.commissionPercentage + dateRangeCommissionPercentage
    }
    return commissionPercentage + dateRangeCommissionPercentage
  }, [dateRangeCommission, commissionPercentage, packageCommission])
  const cappedIncentiveCommissionLabel = <FormattedMessage
    id="dFdqzuHth3rRdhpfoEi8"
    defaultMessage="+ {percentage}% incentive commission (up to <maxAmount></maxAmount>)"
    description="Incentive commission text showing exact commission percentage and capped amount for agent offers"
    values={{
      percentage: incentiveCommissionPercentage,
      maxAmount: () => <FormatCurrency value={incentiveCommissionMaxAmount ?? 0} />,
    }}
  />
  const uncappedIncentiveCommissionLabel = <FormattedMessage
    id="TjrDtKpdPeMzxBsLL4z6"
    defaultMessage="+ {percentage}% incentive commission"
    description="Incentive commission text showing exact commission percentage for uncapped agent offers"
    values={{
      percentage: incentiveCommissionPercentage,
    }}
  />
  const incentiveCommissionLabel =
    incentiveCommissionMaxAmount != null
      ? cappedIncentiveCommissionLabel
      : uncappedIncentiveCommissionLabel

  if (config.agentHub.isEnabled && commissionLoading && !customerView) {
    return <TextLoadingBox typography="body-medium" width="8ch" />
  }

  if (!config.agentHub.isEnabled || (!commissionPercentage && !greatestPackageCommission) || !productType || customerView) {
    return null
  }

  if (kind === 'plain') {
    const plainAmountLabel = includesTaxesAndFees ? (
      <FormattedMessage
        id="M1wTojO8Gv4EYWqux5vI"
        defaultMessage="Earn <currency></currency> commission ({commissionPercentage}%)"
        description="Agent hub commission amount with currency value and percentage"
        values={{
          commissionPercentage: commissionToDisplay,
          currency: () => <FormatCurrency value={offerTotal * (commissionToDisplay / 100)} />,
        }}
      />
    ) : (
      <FormattedMessage
        id="bLcneMqtG8nc5bOX0fTr"
        defaultMessage="Earn <currency></currency> commission ({commissionPercentage}%) (excl. taxes and fees)"
        description="Agent hub commission amount with currency value and percentage excluding taxes and fees"
        values={{
          commissionPercentage: commissionToDisplay,
          currency: () => <FormatCurrency value={offerTotal * (commissionToDisplay / 100)} />,
        }}
      />
    )
    const plainPercentageLabel = includesTaxesAndFees ? (
      <FormattedMessage
        id="CmFBL0P2-B3C4UDUYUZu"
        defaultMessage="Earn {commissionPercentage}% commission"
        description="Agent hub commission percentage only in the travel agent hub portal"
        values={{
          commissionPercentage: commissionToDisplay,
        }}
      />
    ) : (
      <FormattedMessage
        id="IuLcvQLOZh33MaBdEKpI"
        defaultMessage="Earn {commissionPercentage}% commission (excl. taxes and fees)"
        description="Agent hub commission percentage only excluding taxes and fees in the travel agent hub portal"
        values={{
          commissionPercentage: commissionToDisplay,
        }}
      />
    )
    return <>
      {offerTotal > 0 && (
        <Group direction="vertical" gap={4}>
          <BodyTextBlock weight="bold" variant="small" colour="highlight-primary-dark">
            {plainAmountLabel}
          </BodyTextBlock>

          {incentiveCommissionPercentage > 0 && (
            <Group direction="horizontal" gap={4}>
              <UrgencyLabel icon={<SolidFireIcon />} variant="critical1">
                {incentiveCommissionLabel}
              </UrgencyLabel>
            </Group>
          )}
        </Group>
      )}
      {offerTotal === 0 && (
        <BodyTextBlock weight="bold" variant="small" colour="highlight-primary-dark">
          {plainPercentageLabel}
        </BodyTextBlock>
      )}
    </>
  }

  const multipleCommissions = greatestPackageCommission > commissionToDisplay || (Object.values(dateRangesCommission).some(dr => dr.commissionPercentage > 0) && !optionId)

  return <PriceRowCommission
    commissionPercentage={commissionToDisplay}
    productType={productType}
    offerTotal={offerTotal}
    multipleCommissions={multipleCommissions}
    greatestCommission={Math.max(greatestPackageCommission, greatestCommissionWithDateRange, commissionToDisplay)}
    minBaseCommission={minBaseCommission}
    maxBaseCommission={maxBaseCommission}
    includesTaxesAndFees={includesTaxesAndFees}
    incentivePercentage={incentiveCommissionPercentage}
  />
}

export default PriceRowAgentHubCommission
